import { Component, ViewChild, ChangeDetectionStrategy, ChangeDetectorRef } from "@angular/core";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import {
  SystemConfigPromiseService,
  ProfilePromiseActionService,
  UserPromiseLookupService,
  ProfileSettingKeys,
  ActiveUser,
  SysFeatures,
} from "shared/cubiq-services";
import { ProfileUpdateRequestImpl } from "shared/cubiq-model-multi-asset";
///

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { TranslateService } from "@ngx-translate/core";
import { BfxUserSettingsComponent } from "./bfx-user-settings.component";
import {
  IBfxUserSettingsPageSubComponent,
  BfxUserSettingsValidityState,
} from "app/bfx-user-settings/bfx-user-settings-page.component";
import { BfxUserSettingsService, BfxUserSettingsComponentState } from "./bfx-user-settings.service";
import { BfxOrderEntrySettingsComponent } from "./bfx-order-entry-settings.component";

import { BfxAutoCompleteComponent } from "../bfx-element-library/bfx-autocomplete.component";
import { BfxNewOrderMenuService } from "../bfx-common/services/bfx-neworder-menu.service";
import { DashboardMenuItem } from "../bfx-common/models/dashboard-menu-item";

const PROFILE_KEY = ProfileSettingKeys.userOrderEntryPreferencesSettings;

type OrderDefaultType = {
  label: string;
  value: string;
};

@Component({
  selector: "bfx-order-entry-settings-preferences",
  templateUrl: "./html/bfx-order-entry-settings-preferences.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class BfxOrderEntrySettingsPreferencesComponent
  implements IBfxUserSettingsPageSubComponent<BfxOrderEntrySettingsComponent>
{
  /**********************************************************************/
  /* DATA */

  public dataChanged: boolean = false;
  public parentComponent: BfxUserSettingsComponent = null;
  public activeSubComponent: number = 0;
  public categoryComponent: BfxOrderEntrySettingsComponent = null;
  private entryPreferencesValueToUpdate: string = "";

  @ViewChild("newOrderTypeDefaultSelection") newOrderTypeDefaultSelection: BfxAutoCompleteComponent;

  private loadedData: {
    multiplyAmountValue: number;
    newOrderTypeDefault: OrderDefaultType;
  } = {
    multiplyAmountValue: null,
    newOrderTypeDefault: null,
  };

  public multiplyAmount: {
    disable: boolean;
    valueDisable: boolean;
    value: number;
    step: number;
    toggle: boolean;
  } = {
    disable: false,
    valueDisable: true,
    value: 0,
    step: 100,
    toggle: false,
  };
  public newOrderTypeDefault: OrderDefaultType;
  public newOrderTypeDefaultSuggestions: OrderDefaultType[] = [];

  /**********************************************************************/
  /* CONSTRUCTOR */

  constructor(
    public userSettingsService: BfxUserSettingsService,
    public systemConfigService: SystemConfigPromiseService,
    public profileService: ProfilePromiseActionService,
    public userService: UserPromiseLookupService,
    public translationService: TranslateService,
    public cd: ChangeDetectorRef,
    public menuService: BfxNewOrderMenuService,
  ) {
    this.menuService.getMenuProvider().then((items) => {
      this.newOrderTypeDefaultSuggestions = items.map((item: DashboardMenuItem) => {
        return {
          label: item.label,
          value: item.id,
        };
      });
      this.cd.markForCheck();
    });
  }

  /**********************************************************************/
  /* USER SETTINGS METHODS */
  /**********************************************************************/

  /* SETTINGS - INITIALIZE */

  public onTabActivated(event: any): void {}

  public initialize(): void {
    let activeUser: ActiveUser = this.parentComponent.activeUser;
    this.activeSubComponent = this.parentComponent.activeSubCategory;
    if (activeUser.profileSettings && activeUser.profileSettings.has(PROFILE_KEY)) {
      let savedData = activeUser.profileSettings.get(PROFILE_KEY);
      this.loadData(savedData);
    } else {
      // Defaults where no existing profile setting exists
      this.newOrderTypeDefault =
        this.newOrderTypeDefaultSuggestions.length > 0
          ? (this.newOrderTypeDefaultSuggestions.find((item) => item.value === "spot") ??
            this.newOrderTypeDefaultSuggestions[0])
          : null;
    }
  }

  /* SETTINGS - VALIDATE */
  public validate(): Promise<BfxUserSettingsValidityState> {
    return new Promise<BfxUserSettingsValidityState>((resolve, reject) => {
      let state = BfxUserSettingsValidityState.VALID;
      //logger.out.info(`${this.constructor.name}.validate() valid:BfxUserSettingsValidityState.${BfxUserSettingsValidityState[state]}`);
      resolve(state);
    });
  }

  /* SETTINGS - SAVE */
  public save(request: ProfileUpdateRequestImpl): void {
    this.saveData(request);
  }

  public postSave(request: ProfileUpdateRequestImpl): void {
    let activeUser = this.parentComponent.activeUser;
    this.postSaveData(activeUser);
    this.dataChanged = false;
  }

  /* SETTINGS - RESET */
  public reset(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.resetData().then(() => {
        this.cd.detectChanges();
        resolve();
      });
    });
  }

  public postReset(): void {
    this.dataChanged = false;
  }

  /* SETTINGS - CHANGE */
  public pageDataChanged($event: any) {
    this.dataChanged = true;
    this.cd.markForCheck();
    this.parentComponent.validate(this.categoryComponent);
  }

  public hasDataChanged(): boolean {
    return this.dataChanged;
  }

  public onComponentStateChanged(componentState: BfxUserSettingsComponentState): void {}

  public onDataChanged(event: any, items: Array<any>): void {}

  /**********************************************************************/
  /* SETTINGS METHODS - HELPERS */

  private getProfileSettingFromKey(
    profileSettingKey: string,
    request: ProfileUpdateRequestImpl,
  ): CubiqTS.ProfileSettingImpl {
    let profileSettings: Array<any> = request.settings;
    if (!profileSettings) return;
    let savedOrderEntryPreferences: CubiqTS.ProfileSettingImpl = profileSettings.find((setting) => {
      return setting.profileSettingKey == profileSettingKey;
    });
    return savedOrderEntryPreferences;
  }

  private saveData(request: ProfileUpdateRequestImpl): void {
    let savedOrderEntryPreferences: CubiqTS.ProfileSettingImpl = this.getProfileSettingFromKey(
      PROFILE_KEY,
      request,
    );

    let newOrderEntryPreferences = {
      multiplyAmountToggle: this.multiplyAmount.toggle ? "Y" : "N",
      multiplyAmountModel: this.multiplyAmount.value,
      newOrderTypeDefault: this.newOrderTypeDefault?.value ?? null,
    };
    this.loadedData.multiplyAmountValue = this.multiplyAmount.value;
    this.loadedData.newOrderTypeDefault = this.newOrderTypeDefault;
    this.entryPreferencesValueToUpdate = JSON.stringify(newOrderEntryPreferences);

    if (savedOrderEntryPreferences == null) {
      let settingObject: CubiqTS.ProfileSettingImpl = new CubiqTS.ProfileSettingImpl();
      settingObject.profileSettingKey = PROFILE_KEY;
      settingObject.profileSettingValue = this.entryPreferencesValueToUpdate;
      request.settings.push(settingObject);
    } else {
      savedOrderEntryPreferences.profileSettingValue = this.entryPreferencesValueToUpdate;
    }
  }

  private postSaveData(activeUser: any): void {
    if (activeUser.profileSettings) {
      activeUser.profileSettings.set(PROFILE_KEY, this.entryPreferencesValueToUpdate);
    }
  }

  private resetData(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      let activeUser: ActiveUser = this.parentComponent.activeUser;
      if (activeUser.profileSettings && activeUser.profileSettings.has(PROFILE_KEY)) {
        let savedData = activeUser.profileSettings.get(PROFILE_KEY);
        this.loadData(savedData);
        resolve();
      }
    });
  }

  private loadData(savedData: any): void {
    let parsedData = JSON.parse(savedData);

    if (parsedData != null) {
      // process toggle
      if (
        parsedData.hasOwnProperty("multiplyAmountToggle") &&
        typeof parsedData.multiplyAmountToggle === "string"
      ) {
        if (parsedData.multiplyAmountToggle === "Y") {
          this.multiplyAmount.valueDisable = false;
          this.multiplyAmount.toggle = true;
        } else {
          this.multiplyAmount.valueDisable = true;
          this.multiplyAmount.toggle = false;
        }
      }
      // process value
      if (
        parsedData.hasOwnProperty("multiplyAmountModel") &&
        parsedData.multiplyAmountModel !== null
      ) {
        this.multiplyAmount.value = parsedData.multiplyAmountModel;
        this.loadedData.multiplyAmountValue = parsedData.multiplyAmountModel;
      }
      if (parsedData.newOrderTypeDefault) {
        const item = this.newOrderTypeDefaultSuggestions.find(
          (element) => element.value == parsedData.newOrderTypeDefault,
        );
        if (item != null) {
          this.newOrderTypeDefault = item;
        }
      }
      if (!this.newOrderTypeDefault) {
        // User may not be able to create any orders
        this.newOrderTypeDefault =
          this.newOrderTypeDefaultSuggestions.length > 0
            ? this.newOrderTypeDefaultSuggestions[0]
            : null;
      }

      this.loadedData.newOrderTypeDefault = this.newOrderTypeDefault;
    }
    this.cd.markForCheck();
  }

  /**********************************************************************/
  /* THE MEAT METHODS */

  public multiplyAmountValueBlur($event: any): void {
    if (this.detectChanges()) {
      this.pageDataChanged($event);
    }
  }

  public multiplyAmountToggleChanged($event: boolean): void {
    if ($event) {
      this.multiplyAmount.valueDisable = false;
    } else {
      this.multiplyAmount.valueDisable = true;
    }
    this.pageDataChanged($event);
  }

  private detectChanges(): boolean {
    return this.multiplyAmount.value != this.loadedData.multiplyAmountValue;
  }

  private detectNewOrderTypeDefaultChange(): boolean {
    return this.newOrderTypeDefault != this.loadedData.newOrderTypeDefault;
  }

  public searchNewOrderTypeDefault(event: any): void {
    this.newOrderTypeDefaultSuggestions = [...this.newOrderTypeDefaultSuggestions];
  }

  public onBlurAutocomplete(autocomplete: BfxAutoCompleteComponent): void {
    if (autocomplete === this.newOrderTypeDefaultSelection) {
      if (this.detectNewOrderTypeDefaultChange()) {
        this.dataChanged = true;
        this.cd.markForCheck();
        this.parentComponent.validate(this.categoryComponent);
      }
    }
  }

  public onSelectNewOrderTypeDefault(data: any): void {
    if (this.detectNewOrderTypeDefaultChange()) {
      this.dataChanged = true;
      this.newOrderTypeDefault = data;
      this.cd.markForCheck();
      this.parentComponent.validate(this.categoryComponent);
    }
  }
}
